import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Loader2, RefreshCw, Mail, User, CheckCircle, AlertTriangle } from "lucide-react";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

export default function EditProfileForm({ admin, onUpdate, onMessage }) {
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [message, setMessage] = useState("");
  const [resending, setResending] = useState(false);
  const [saving, setSaving] = useState(false);

  const token = JSON.parse(localStorage.getItem("user"))?.token;
  const isEditingRef = useRef(false);
  const prevAdminRef = useRef();

  // Initialize fields once when admin changes fully (not on every rerender)
  useEffect(() => {
    // Avoid resetting inputs if user currently editing
    if (prevAdminRef.current !== admin && !isEditingRef.current) {
      setEmail(admin.email || "");
      setUsername(admin.username || "");
      prevAdminRef.current = admin;
    }
  }, [admin]);

  // Refetch when pendingEmail cleared
  useEffect(() => {
    if (!admin.pendingEmail) {
      onUpdate();
    }
  }, [admin.pendingEmail, onUpdate]);

  // Send message to parent and clear local message
  useEffect(() => {
    if (message) {
      onMessage?.(message);
      const timeout = setTimeout(() => setMessage(""), 4000);
      return () => clearTimeout(timeout);
    }
  }, [message, onMessage]);

  const handleEmailChange = (e) => {
    isEditingRef.current = true;
    setEmail(e.target.value);
  };

  const handleUsernameChange = (e) => {
    isEditingRef.current = true;
    setUsername(e.target.value);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();

    if (!token) {
      setMessage("❌ Token missing. Please log in again.");
      return;
    }

    setSaving(true);
    try {
      const cleanEmail = email.trim().toLowerCase();
      const cleanUsername = username.trim();

      // Proceed to update profile
      const res = await axios.put(
        `${API_BASE}/api/admin/profile/update`,
        { email: cleanEmail, username: cleanUsername },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = res.data;
      setMessage(`✅ ${data.message || "Profile updated successfully."}`);
      isEditingRef.current = false; // Reset editing flag after update
      onUpdate();
    } catch (err) {
      setMessage(err.response?.data?.message || "❌ Update failed.");
    } finally {
      setSaving(false);
    }
  };

  const handleResend = async () => {
    if (!token) return;
    setResending(true);
    try {
      const res = await axios.post(
        `${API_BASE}/api/admin/profile/resend-email-verification`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = res.data;
      setMessage(`✅ ${data.message || "Verification email resent."}`);
    } catch (err) {
      setMessage(
        err.response?.data?.message || "❌ Failed to resend verification email."
      );
    } finally {
      setResending(false);
    }
  };

  return (
    <form
      onSubmit={handleUpdate}
      className="bg-white dark:bg-gray-900 p-8 rounded-2xl shadow-lg max-w-xl mx-auto mt-8 space-y-8 border border-blue-200 dark:border-blue-900"
      autoComplete="off"
    >
      <h3 className="text-3xl font-bold text-blue-600 dark:text-blue-300 mb-2 flex items-center gap-2">
        <User className="w-7 h-7" /> Edit Profile
      </h3>

      {message && (
        <div
          className={`text-sm px-3 py-2 rounded-lg mb-2 flex gap-1 items-center font-medium ${
            message.startsWith("✅")
              ? "bg-green-50 text-green-700 dark:bg-green-900/80 dark:text-green-200"
              : "bg-red-50 text-red-700 dark:bg-red-900/80 dark:text-red-200"
          }`}
        >
          {message.startsWith("✅") ? (
            <CheckCircle className="w-4 h-4" />
          ) : (
            <AlertTriangle className="w-4 h-4" />
          )}
          {message}
        </div>
      )}

      <div>
        <div className="mb-1 text-gray-600 dark:text-gray-300 text-sm">Current Email</div>
        <div className="flex items-center gap-2 text-base">
          <Mail className="w-4 h-4 text-gray-400" />
          <span className="font-semibold">{admin.email}</span>
          {admin.emailVerified ? (
            <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-green-200 dark:bg-green-800 text-green-800 dark:text-green-200 rounded">
              Verified
            </span>
          ) : (
            <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-yellow-200 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 rounded">
              Not Verified
            </span>
          )}
        </div>
        {admin.pendingEmail && (
          <div className="flex items-center gap-2 mt-1 text-yellow-700 dark:text-yellow-200 text-sm">
            <AlertTriangle className="w-4 h-4" />
            <span className="font-medium">Pending New Email:</span>
            <span>{admin.pendingEmail} (awaiting verification)</span>
          </div>
        )}
      </div>

      <div>
        <label className="block text-base font-semibold mb-1 text-blue-800 dark:text-blue-200">
          New Email
        </label>
        <div className="relative">
          <Mail className="w-5 h-5 absolute left-3 top-2.5 text-gray-400" />
          <input
            type="email"
            value={email}
            onChange={handleEmailChange}
            className="w-full pl-12 p-3 rounded-lg border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 text-base transition"
            placeholder="Enter new email"
            required
            autoComplete="off"
          />
        </div>
        {admin.pendingEmail && (
          <button
            type="button"
            onClick={handleResend}
            disabled={resending}
            className="mt-3 inline-flex items-center bg-yellow-400 dark:bg-yellow-700 hover:bg-yellow-500 dark:hover:bg-yellow-600 text-gray-900 font-semibold py-2 px-6 rounded-lg text-sm transition disabled:opacity-60"
          >
            {resending ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            {resending ? "Resending..." : "Resend Verification"}
          </button>
        )}
      </div>

      <div>
        <label className="block text-base font-semibold mb-1 text-blue-800 dark:text-blue-200">
          New Username
        </label>
        <div className="relative">
          <User className="w-5 h-5 absolute left-3 top-2.5 text-gray-400" />
          <input
            value={username}
            onChange={handleUsernameChange}
            className="w-full pl-12 p-3 rounded-lg border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 text-base transition"
            placeholder="Enter new username"
            required
            autoComplete="off"
          />
        </div>
      </div>

      <button
        type="submit"
        disabled={saving}
        className="w-full flex items-center justify-center bg-blue-700 dark:bg-blue-500 text-white font-semibold py-3 rounded-lg shadow hover:bg-blue-800 dark:hover:bg-blue-600 transition disabled:opacity-60"
      >
        {saving && <Loader2 className="w-5 h-5 mr-2 animate-spin" />}
        {saving ? "Saving..." : "Save Changes"}
      </button>
    </form>
  );
}
